import { ref, computed, onMounted } from 'vue';
import api from '../services/api';

/**
 * Composable per la gestione del billing utente
 * Gestisce lo stato, il fetch e la sincronizzazione dei dati di fatturazione
 *
 * NOTA: Le carte di pagamento vengono gestite tramite Stripe Customer Portal.
 * L'utente può aggiungere/togliere carte da lì. L'autoricarica usa il
 * default payment method del customer Stripe.
 *
 * @param {Ref<string>|string} workspaceId - WorkspaceId (computed ref or plain string)
 * @param {Function} t - Funzione di traduzione i18n (opzionale)
 */
export function useBilling(workspaceId, t = null) {
  // State
  const summary = ref(null);
  const autoRechargeConfig = ref(null);
  const purchases = ref([]);
  const paymentMethods = ref([]);
  const defaultPaymentMethod = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const savingAutoRecharge = ref(false);

  // Costanti
  const RICARICA_SCALINO_EUR = 20;

  /**
   * Fetch riepilogo billing dell'utente
   */
  const fetchSummary = async () => {
    loading.value = true;
    error.value = null;

    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/summary`);
      
      if (response.data.success) {
        summary.value = response.data.data;
      } else {
        error.value = response.data.error?.message || (t ? t('billing.errors.loadSummary') : 'Errore nel caricamento del riepilogo billing');
      }
    } catch (err) {
      Console.error('[useBilling] Error fetching summary:', err);
      error.value = err.response?.data?.error?.message || err.message || (t ? t('billing.errors.loadSummary') : 'Errore nel caricamento del riepilogo billing');
    } finally {
      loading.value = false;
    }
  };

  /**
   * Fetch lista acquisti
   */
  const fetchPurchases = async () => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/purchases`);
      
      if (response.data.success) {
        purchases.value = response.data.data.purchases || [];
      }
    } catch (err) {
      Console.error('[useBilling] Error fetching purchases:', err);
    }
  };

  /**
   * Fetch configurazione auto-recharge
   */
  const fetchAutoRechargeConfig = async () => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/auto-recharge`);
      
      if (response.data.success) {
        autoRechargeConfig.value = response.data.data;
      }
    } catch (err) {
      Console.error('[useBilling] Error fetching auto-recharge config:', err);
    }
  };

  /**
   * Fetch carte di pagamento salvate
   */
  const fetchPaymentMethods = async () => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/payment-methods`);
      
      if (response.data.success) {
        paymentMethods.value = response.data.data.paymentMethods || [];
        defaultPaymentMethod.value = response.data.data.defaultPaymentMethod;
      }
    } catch (err) {
      Console.error('[useBilling] Error fetching payment methods:', err);
    }
  };

  /**
   * Salva configurazione auto-recharge
   */
  const saveAutoRecharge = async (config) => {
    savingAutoRecharge.value = true;
    error.value = null;

    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.put(`/workspaces/${wsId}/billing/auto-recharge`, config);
      
      if (response.data.success) {
        autoRechargeConfig.value = response.data.data;
        return { success: true };
      } else {
        error.value = response.data.error?.message || (t ? t('billing.errors.saveConfig') : 'Errore nel salvataggio della configurazione');
        return { success: false, error: error.value };
      }
    } catch (err) {
      Console.error('[useBilling] Error saving auto-recharge:', err);
      error.value = err.response?.data?.error?.message || err.message || (t ? t('billing.errors.saveConfig') : 'Errore nel salvataggio della configurazione');
      return { success: false, error: error.value };
    } finally {
      savingAutoRecharge.value = false;
    }
  };

  /**
   * Toggle auto-recharge on/off
   */
  const toggleAutoRecharge = async (enabled) => {
    return saveAutoRecharge({ enabled });
  };

  /**
   * Crea sessione checkout Stripe per acquisto token
   */
  const createCheckoutSession = async (amountEur, isActivation = false) => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.post(`/workspaces/${wsId}/billing/checkout-session`, { amountEur, isActivation });
      
      if (response.data.success && response.data.data?.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.data.checkoutUrl;
        return { success: true };
      } else {
        return { success: false, error: response.data.error?.message || (t ? t('billing.errors.createCheckout') : 'Errore nella creazione della sessione checkout') };
      }
    } catch (err) {
      Console.error('[useBilling] Error creating checkout session:', err);
      return { success: false, error: err.response?.data?.error?.message || err.message || (t ? t('billing.errors.createCheckout') : 'Errore nella creazione della sessione checkout') };
    }
  };

  /**
   * Paga direttamente con la carta salvata (senza aprire Stripe Checkout)
   */
  const payWithSavedCard = async (amountEur) => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.post(`/workspaces/${wsId}/billing/pay-with-saved-card`, { amountEur });
      
      if (response.data.success) {
        const { status } = response.data.data;
        if (status === 'succeeded') {
          await fetchSummary();
          await fetchPurchases();
          return { success: true, completed: true };
        }
        return { success: true, completed: false, status };
      } else {
        return { success: false, error: response.data.error?.message || 'Errore nel pagamento' };
      }
    } catch (err) {
      Console.error('[useBilling] Error paying with saved card:', err);
      return { success: false, error: err.response?.data?.error?.message || err.message || 'Errore nel pagamento' };
    }
  };

  /**
   * Apre il Customer Portal di Stripe per gestire le carte di pagamento
   * L'utente può aggiungere, rimuovere o impostare come default le sue carte
   */
  const openStripePortal = async () => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/customer-portal`);
      
      if (response.data.success && response.data.data?.portalUrl) {
        window.location.href = response.data.data.portalUrl;
        return { success: true };
      } else {
        return { success: false, error: response.data.error?.message || (t ? t('billing.errors.openPortal') : "Errore nell'apertura del portale Stripe") };
      }
    } catch (err) {
      Console.error('[useBilling] Error opening Stripe portal:', err);
      return { success: false, error: err.response?.data?.error?.message || err.message || (t ? t('billing.errors.openPortal') : "Errore nell'apertura del portale Stripe") };
    }
  };

  /**
   * Ottiene la publishable key di Stripe dal backend
   */
  const fetchStripePublishableKey = async () => {
    try {
      const wsId = (typeof workspaceId === 'object' ? workspaceId.value : workspaceId);
      const response = await api.get(`/workspaces/${wsId}/billing/config`);
      if (response.data.success && response.data.data?.stripePublishableKey) {
        return response.data.data.stripePublishableKey;
      }
    } catch (err) {
      Console.error('[useBilling] Error fetching Stripe publishable key:', err);
    }
    return null;
  };

  const totalTokens = computed(() => {
    if (!summary.value) return 0;
    // Balance = token RIMANENTI (tokenAllowance - tokenUsed)
    return summary.value.balance || 0;
  });

  // Totale token acquistati cumulativo
  const totalAllowance = computed(() => {
    if (!summary.value) return 0;
    return summary.value.totalAllowance || 0;
  });

  // Token consumati
  const tokenUsed = computed(() => {
    if (!summary.value) return 0;
    return summary.value.tokenUsed || 0;
  });

  const monthlySpending = computed(() => {
    if (!summary.value) return { spent: 0, cap: 0, percentage: 0 };
    const spent = Math.round((summary.value.monthlySpent || 0) * 100) / 100;
    const cap = Math.round((summary.value.monthlyCap || 100) * 100) / 100;
    const percentage = cap > 0 ? Math.round(Math.min((spent / cap) * 100, 100) * 100) / 100 : 0;
    return { spent, cap, percentage };
  });

  const autoRechargeEnabled = computed(() => {
    return autoRechargeConfig.value?.enabled ?? false;
  });

  // Token multiplier (tokens per €1) - default 5_000_000, arrotondato a 2 decimali
  const tokenMultiplier = computed(() => {
    const multiplier = autoRechargeConfig.value?.tokenMultiplier ?? 5_000_000;
    return Math.round(multiplier * 100) / 100; // 2 decimali
  });

  // Calcola il numero massimo di ricariche mensili basato sul cap (€50 per ricarica)
  const maxMonthlyRecharges = computed(() => {
    const cap = autoRechargeConfig.value?.monthlyCapEur || 100;
    return Math.floor(cap / RICARICA_SCALINO_EUR);
  });

  // Calcola i token stimati totali per il cap mensile
  // tokens = (cap / €20) * €20 * tokenMultiplier = cap * tokenMultiplier
  const estimatedTokensPerMonth = computed(() => {
    const cap = autoRechargeConfig.value?.monthlyCapEur || 0;
    if (!cap || cap < RICARICA_SCALINO_EUR) return 0;
    const numRecharges = Math.floor(cap / RICARICA_SCALINO_EUR);
    const totalEur = numRecharges * RICARICA_SCALINO_EUR;
    const multiplier = Math.round((tokenMultiplier.value) * 100) / 100;
    return Math.floor(totalEur * multiplier);
  });

  // Verifica se c'è una carta predefinita
  const hasDefaultCard = computed(() => {
    return !!defaultPaymentMethod.value;
  });

  // Fetch initial data
  onMounted(() => {
    fetchSummary();
    fetchPurchases();
    fetchAutoRechargeConfig();
    fetchPaymentMethods();
  });

  return {
    // State
    summary,
    autoRechargeConfig,
    purchases,
    paymentMethods,
    defaultPaymentMethod,
    loading,
    error,
    savingAutoRecharge,

    // Computed
    totalTokens,
    totalAllowance,
    tokenUsed,
    monthlySpending,
    autoRechargeEnabled,
    tokenMultiplier,
    maxMonthlyRecharges,
    estimatedTokensPerMonth,
    hasDefaultCard,

    // Constants
    RICARICA_SCALINO_EUR,

    // Methods
    fetchSummary,
    fetchPurchases,
    fetchAutoRechargeConfig,
    fetchPaymentMethods,
    saveAutoRecharge,
    toggleAutoRecharge,
    createCheckoutSession,
    payWithSavedCard,
    openStripePortal,
    fetchStripePublishableKey,
  };
}

export default useBilling;
